const { AnonymousCredential, BlobServiceClient, newPipeline } = require("@azure/storage-blob");

//---------------------------------------------------------------------------------------------
// Description:
//  - Wrapper around the @azure/storage-blob functionality to support uploading files to ABS.
//    This Library abstracts service-request and resale-owner-verification functionality
//    and handles file group* naming.
//---------------------------------------------------------------------------------------------
export function AzureBlobStorageHelper() {
    let obj = {
        initialize: initialize,
        uri: "",
        createFileGroup: createFileGroup,
        uploadFiles: uploadFiles,
        adId: "",
        isMSHLoggedIn: false,
        containerName: ""
    };
    return obj;

    //---------------------------------------------------------------------------------------------
    // Description:
    //  - Initializes the AzureBlobStorageHelper object for use with service-request or
    //    resale-owner-verification scenarios
    // Parameters:
    //    - storageDomain = the fully qualified domain name of the azure blob storage account
    //    - containerName = the name of the container to store blob objects
    //    - sas = shared access token
    //    - adId = the adId of the user that the uploaded assets will be associated to
    //    - isMSHLoggedIn = true if the user is uploading assets as a logged in msh user, false otherwise
    //    - accessDomain = the fully qualified domain name of where images will be accessed from
    //      accessDomain contains a web application that receives essentially the url to azure and internally
    //      generates a sasToken to allow access the Azure Blob.
    // Notes:
    //    - UI implementation current gets a SAS token in the cookie from the server which
    //      is subsequently passed down into this method
    //---------------------------------------------------------------------------------------------
    function initialize(storageDomain, containerName, sas, adId, isMSHLoggedIn, accessDomain) {
        let pipeline = newPipeline(new AnonymousCredential(), {
            retryOptions: { maxTries: 3 }, // Retry options
            userAgentOptions: { userAgentPrefix: "JFS AzureBlobHelper V1.0.0" }, // Customized telemetry string
            keepAliveOptions: {
                // Keep alive is enabled by default, disable keep alive by setting false
                enable: false
            }
        });

        if (adId) 
        {
            obj.adId = adId;
        }
        else
        {
            return false;
        }

        if (containerName)
        {
            obj.containerName = containerName;
        }
        else
        {
            return false;
        }

        if (obj.containerName == "service-request" || obj.containerName == "service-request-non-prod") {
            obj.type = "sr";
        }
        else if (obj.containerName == "warranty-request-public" || obj.containerName == "warranty-request-public-non-prod") {
            obj.type = "wr";
        }        
        else if (obj.containerName == "resale-owner-verification" || obj.containerName == "resale-owner-verification-non-prod") {
            obj.type = "rov";
        }
        else
        {
            console.error("Unsupported containerName: " + obj.containerName);
            return false;
        }

        if (accessDomain)
        {
            obj.accessUri = 'https://' + accessDomain
        }
        else
        {
            return false;
        }

        if (!sas)
        {
            return false;
        }
        
        if (storageDomain)
        {
            obj.uri = 'https://' + storageDomain;   // url to azure storage servers
        }
        else
        {
            return false;
        }

        obj.isMSHLoggedIn = isMSHLoggedIn;
        
        if (sas && sas[0] != '?') { sas = '?' + sas; }
        console.log(obj.uri + sas);
        obj.blobServiceClient = new BlobServiceClient(
            obj.uri + sas,
            pipeline
        );

        obj.containerClient = obj.blobServiceClient.getContainerClient(obj.containerName);
        return true;
    }

    //---------------------------------------------------------------------------------------------
    // Description
    //  - createFileGroup disambiguates files uploaded to Azure Blob Storage.  The file groups
    //    appears like a folder but only exists if there's is a blob.  If you remove all blobs
    //    belonging to a file group, the file group is removed.  File group is not an Azure
    //    or microsoft term.
    //  - function returns the 'folder' for the file in the form:
    //    YYYYMM/adid/sr-LotCostCenter-YYYYMMDD-HHMMSS-OriginalFilename.txt or 
    //    YYYYMM/adid/wr-LotCostCenter-YYYYMMDD-HHMMSS-OriginalFilename.txt or
    //    YYYYMM/adid/rov-YYYYMMDD-HHMMSS-OriginalFilename.txt
    //  Ref: https://jfshea.atlassian.net/wiki/spaces/STWRDP2/pages/2329706578/Azure+Blob+Storage+-+System+Requirements
    //---------------------------------------------------------------------------------------------
    function createFileGroup(adId, lotCostCenter) {
        return new Promise(function(resolve, reject) {
            try
            {
                obj.date = new Date();
                if (obj.containerName == "service-request" || obj.containerName == "service-request-non-prod") {
                    if (resolve) resolve(getDateFormat(obj.date, obj.type, adId, lotCostCenter));
                }
                else if (obj.containerName == "warranty-request-public" || obj.containerName == "warranty-request-public-non-prod") {
                    if (resolve) resolve(getDateFormat(obj.date, obj.type, adId, lotCostCenter));
                }
                else if (obj.containerName == "resale-owner-verification" || obj.containerName == "resale-owner-verification-non-prod") {
                    if (resolve) resolve(getDateFormat(obj.date, obj.type, adId, lotCostCenter));
                }
                else {
                    if (resolve) resolve(getDateFormat(obj.date, "unknown", adId, lotCostCenter));
                }
            }
            catch (err)
            {
                if (reject) reject(err);
            }
        });
    };

    //---------------------------------------------------------------------------------------------
    // fileGroup: name of the fileGroup prepended to the blob
    // files: array of files from the input element files property
    //---------------------------------------------------------------------------------------------
    function uploadFiles(fileGroup, files, lotCostCenter) {
        return new Promise(function(resolve, reject) {
            try
            {
                const promises = [];
                const fileUris = [];
                for (const file of files) {
                    let filename = fileGroup + updateFilename(obj.date, obj.type, file.name, lotCostCenter);
                    let blockBlobClient = obj.containerClient.getBlockBlobClient(filename);
                    let promise = blockBlobClient.uploadBrowserData(file, {
                        onProgress: file.onProgress
                    });
                    promise.azureUri = obj.uri + "/" + obj.containerName + "/" + filename;
                    promise.fileUri = obj.accessUri + "/" + obj.containerName + "/" + filename;
                    promise.id = file.id;
                    promises.push(promise);
                    promise.then(function() {
                        // // file upload succeeded
                        // // Set the Tags
                        // var tags = {};
                        // var adId = obj.adId;
                        // if (adId && adId.length > 0) tags.ADID = adId;
                        // if (lotCostCenter && lotCostCenter.length > 0) tags.LotCostCenter = lotCostCenter;
                        // blockBlobClient.setTags(tags).then(function(response) {
                        //     fileUris.push({
                        //         uri: promise.fileUri,
                        //         containerName: obj.containerName,
                        //         filename: filename
                        //         }
                        //     );
                        // });
                        fileUris.push({
                            id: promise.id,
                            uri: promise.fileUri,
                            containerName: obj.containerName,
                            filename: filename
                            }
                        );
                    },function(){
                        fileUris.push({
                                id: promise.id,
                                uri: promise.fileUri,
                                containerName: obj.containerName,
                                filename: filename,
                                error: true,
                                errorMsg: "Upload failed for file"
                            }
                        );
                    });
                }
                Promise.all(promises).then(function(result)
                {
                    // sort the file in the order it was passed in instead of the order download completed
                    fileUris.sort(function(a, b) { return a.id - b.id; });
                    if (resolve) resolve(fileUris);
                }, function(err){
                    if (reject) reject(err);
                });
            }
            catch (err) {
                console.log("azure-blob-storage-helper error: " + error);
                if (reject) reject(err);
            }
        });
    }

    // function setBlobTags(blobUrl, adId, lotCostCenter) {
    //     var url = blobUrl + "?comp=tags";
    //     var tags = [];
    //     if (adId && adId.length > 0) tags.push({ Key: "ADID", Value: adId });
    //     if (lotCostCenter && lotCostCenter.length > 0) tags.push({ Key: "LotCostCenter", Value: lotCostCenter });
    //     var tagXml = ConvertToTagXml(tags);

    //     $.ajax({
    //         url: blobUrl + "?comp=tags",
    //         type: 'PUT',
    //         data: tagXml,
    //         success: function(data) {
    //         }
    //     });
    // }

    // //---------------------------------------------------------------------------------------------
    // // Returns the XML string for the blob tags.  Used in the post to the blob to set the tags
    // // See: https://docs.microsoft.com/en-us/rest/api/storageservices/set-blob-tags
    // // keyValue is an array of { Key: "Key1", Value: "Value1" } objects.
    // // http PUt is used to set the tags:
    // // https://myaccount.blob.core.windows.net/mycontainer/myblob?comp=tags
    // //---------------------------------------------------------------------------------------------
    // function ConvertToTagXml(keyValues) {
    //     var xml = "<?xml version=\"1.0\" encoding=\"utf-8\"?>";
    //     xml += "<Tags><TagSet>";
    //     for (var i = 0; i < keyValues.length; i++) {
    //       var value = keyValues[i];
    //       xml += "<Tag>";
    //       xml += "<Key>" + value.Key + "</Key>";
    //       xml += "<Value>" + value.Value + "</Value>";
    //       xml += "</Tag>";
    //     }
    //     xml += "</TagSet></Tags>";
    //     return xml;
    // }

    //---------------------------------------------------------------------------------------------
    // Description
    //  - method formats the date for use with when creating a file group.
    //---------------------------------------------------------------------------------------------
    function getDateFormat(d, type, adId, lotCostCenter) {
        if (!d) {
            d = new Date();
        }
        if (!type || type=="")
        {
            type = "unknown";
        }
        var month = '' + (d.getMonth() + 1),
            day = '' + d.getDate(),
            year = '' + d.getFullYear(),
            hours = '' + d.getHours(),
            minutes = '' + d.getMinutes(),
            seconds = '' + d.getSeconds();

        if (month.length < 2) month = '0' + month;
        if (day.length < 2) day = '0' + day;
        if (hours.length < 2) hours = '0' + hours;
        if (minutes.length < 2) minutes = '0' + minutes;
        if (seconds.length < 2) seconds = '0' + seconds;

        var folderMonth = [year, month].join('');
        return folderMonth + "/" + adId + "/";
    }

    //---------------------------------------------------------------------------------------------
    // Add date to filename
    // Convert non-ascii characters to _
    //---------------------------------------------------------------------------------------------
    function updateFilename(d, type, filename, lotCostCenter)
    {
        try
        {
            if (!d) {
                d = new Date();
            }
            var month = '' + (d.getMonth() + 1),
                day = '' + d.getDate(),
                year = '' + d.getFullYear(),
                hours = '' + d.getHours(),
                minutes = '' + d.getMinutes(),
                seconds = '' + d.getSeconds(),
                ms = '' + d.getMilliseconds();
    
                if (month.length < 2) month = '0' + month;
                if (day.length < 2) day = '0' + day;
                if (hours.length < 2) hours = '0' + hours;
                if (minutes.length < 2) minutes = '0' + minutes;
                if (seconds.length < 2) seconds = '0' + seconds;
                if (ms.length == 1) ms = '00' + ms;
                if (ms.length == 2) ms = '0' + ms;

            // Replace non-alphanumeric characters with underscore to prevent encoding
            // issues in the Url
            var filenameLen = filename.length;
            var index = filename.lastIndexOf('.');
            if (index > -1)
            {
                var partFile = filename.substring(0, index);
                var partExt = filename.substring(index, index + (filenameLen - index));
                partFile = partFile.replace(/[^0-9a-zA-Z.]+/g, "_");
                partFile = partFile.replace(/[_]+/g, "_");
                partFile = partFile.replace(/(^_|_$)/g, "");
                partExt = partExt.replace(/[_]+/g, "_");
                partExt = partExt.replace(/(^_|_$)/g, "");
                filename = partFile + partExt;
            }
            else
            {
                var partFile = filename;
                var partExt = "";
                partFile = partFile.replace(/[^0-9a-zA-Z.]+/g, "_");
                partFile = partFile.replace(/[_]+/g, "_");
                partFile = partFile.replace(/(^_|_$)/g, "");
                partExt = partExt.replace(/[_]+/g, "_");
                partExt = partExt.replace(/(^_|_$)/g, "");
                filename = partFile + partExt;
            }

            if (type=="wr") return "wr-" + lotCostCenter + "-" + [year, month, day].join('') + "-" + [hours, minutes, seconds].join('') + "-" + filename;
            if (type=="sr") return "sr-" + lotCostCenter + "-" + [year, month, day].join('') + "-" + [hours, minutes, seconds].join('') + "-" + filename;
            if (type=="rov") return "rov-" + [year, month, day].join('') + "-" + [hours, minutes, seconds].join('') + "-" + filename;
            throw "Unrecognized type" + type;
        } catch (err)
        {
            console.log(err);
        }
        return filename;
    };
}
